// The shop catalog: every purchasable item, with server-authoritative prices.
// Lives in core so the client (shop page, pickers) and the edge-server import
// the exact same file - a purchase is always priced from THIS catalog looked
// up by id, never from a client-submitted price.
//
// The economy: real money buys ONLY Golden Egg packs (Stripe); cosmetics and
// effects are priced in eggs by RARITY and bought with the buy-with-eggs
// route. Egg costs track the base pack rate (100 eggs ~ $5), climbing the
// same black/turquoise/purple/gold ladder the pack cards wear.
//
// Item ids are literally the wire values the cosmetic systems already use
// (player.victoryAnim types / flag codes), so ownership checks need no id
// translation anywhere.
//
// Free items are simply ABSENT from the catalog: isPurchasable() === false is
// the "always allowed" signal (country flags stay free).

// Each rarity is a BAND: items carry their own costEggs within it, so two
// rares can differ in price while the card colour/hover stays per-rarity.
var RARITIES = {
    common: { label: "Common", min: 50, max: 120 },
    rare: { label: "Rare", min: 150, max: 250 },
    epic: { label: "Epic", min: 300, max: 450 },
    legendary: { label: "Legendary", min: 500, max: 2000 },
};

var ITEMS = [
    // Victory animations (see the client's game-canvas/victory-anim.ts).
    { id: "flag_plant", kind: "victory", label: "Hoist the Flag", rarity: "rare", costEggs: 230 },
    { id: "akimbo", kind: "victory", label: "Gunslinger", rarity: "epic", costEggs: 450 },
    { id: "fireworks", kind: "victory", label: "Fireworks", rarity: "rare", costEggs: 250 },
    { id: "disco", kind: "victory", label: "Disco", rarity: "legendary", costEggs: 600 },
    { id: "coronation", kind: "victory", label: "Coronation", rarity: "epic", costEggs: 400 },
    { id: "trophy", kind: "victory", label: "Golden Trophy", rarity: "epic", costEggs: 350 },
    { id: "champagne", kind: "victory", label: "Champagne Shower", rarity: "legendary", costEggs: 1000 },

    // Worm heads (see the client's game-canvas/head-styles.ts).
    { id: "smiley", kind: "head", label: "Smiley", rarity: "common", costEggs: 90 },
    { id: "skull", kind: "head", label: "Skull", rarity: "common", costEggs: 100 },
    { id: "pumpkin", kind: "head", label: "Pumpkin", rarity: "rare", costEggs: 220 },
    { id: "ghoul", kind: "head", label: "Ghoul", rarity: "rare", costEggs: 240 },
    { id: "fireball", kind: "head", label: "Fireball", rarity: "epic", costEggs: 300 },
    { id: "robot", kind: "head", label: "Robot", rarity: "epic", costEggs: 320 },
    { id: "missile", kind: "head", label: "Missile", rarity: "epic", costEggs: 400 },
    { id: "rocket", kind: "head", label: "Space Ship", rarity: "legendary", costEggs: 500 },
    { id: "pacman", kind: "head", label: "Pac-Worm", rarity: "legendary", costEggs: 550 },
    { id: "goldegg", kind: "head", label: "Golden Egg Head", rarity: "legendary", costEggs: 1000 },
    { id: "viking", kind: "head", label: "Viking", rarity: "epic", costEggs: 380 },
    { id: "rich-viking", kind: "head", label: "Rich Viking", rarity: "legendary", costEggs: 700 },
    // Not for sale: `exclusive` items are granted (the Royale's crown comes with
    // your first Royale win, see royale-rewards.js). No price, so they can't be
    // bought and never turn up in a filled seat's wardrobe.
    { id: "crown", kind: "head", label: "Royale Laurel", rarity: "legendary", exclusive: "royale" },

    // Board skins (see the client's game-canvas/board-skins.ts; viewer-local).
    { id: "midnight", kind: "board", label: "Midnight Blue", rarity: "common", costEggs: 90 },
    { id: "ember", kind: "board", label: "Ember", rarity: "common", costEggs: 100 },
    { id: "mono", kind: "board", label: "Monochrome", rarity: "common", costEggs: 100 },
    { id: "retro", kind: "board", label: "Retro Green", rarity: "rare", costEggs: 180 },
    { id: "checker", kind: "board", label: "Chess", rarity: "rare", costEggs: 200 },
    { id: "hive", kind: "board", label: "Bee Hive", rarity: "epic", costEggs: 320 },
    { id: "nebula", kind: "board", label: "Nebula", rarity: "epic", costEggs: 350 },
    { id: "dungeon", kind: "board", label: "Dungeon", rarity: "legendary", costEggs: 550 },
    { id: "gold", kind: "board", label: "I am rich", rarity: "legendary", costEggs: 1000 },

    // Novelty flags (assets in the client's /flags + /flags-square).
    { id: "checkered", kind: "flag", label: "Finish Line", rarity: "common", costEggs: 60 },
    { id: "moon", kind: "flag", label: "Moon", rarity: "rare", costEggs: 150 },
    { id: "pizza", kind: "flag", label: "Pizza Slice", rarity: "rare", costEggs: 150 },
    // animated flags (CSS inside the SVG): the top of the flag ladder
    { id: "pirate", kind: "flag", label: "Jolly Roger", rarity: "epic", costEggs: 300 },
    { id: "earth", kind: "flag", label: "Planet Earth", rarity: "epic", costEggs: 350 },
    { id: "duck", kind: "flag", label: "Rubber Duck", rarity: "legendary", costEggs: 500 },
    { id: "goldenegg", kind: "flag", label: "The Golden Egg", rarity: "legendary", costEggs: 1000 },
    { id: "goodboy", kind: "flag", label: "Good Boy", rarity: "common", costEggs: 60 },
    { id: "frog", kind: "flag", label: "Frog", rarity: "rare", costEggs: 150 },
    { id: "catnoir", kind: "flag", label: "Cat Noir", rarity: "common", costEggs: 80 },
    { id: "shuriken", kind: "flag", label: "Shuriken", rarity: "epic", costEggs: 320 },
    { id: "shadow", kind: "flag", label: "Shadow", rarity: "rare", costEggs: 160 },
    { id: "illuminati", kind: "flag", label: "Illuminati", rarity: "legendary", costEggs: 550 },

    // Golden Egg packs (the ONLY real-money purchases). Repeatable: grants go
    // to the egg_ledger (one row per Stripe session), never to shop_purchases,
    // so the same pack can be bought again and again. A pack grants EXACTLY
    // `eggs`; `bonus` is value messaging ("+500 eggs" = how many more you get
    // than buying the same money's worth of Hatchlings), never additive.
    { id: "eggs_hatchling", kind: "eggs", label: "Hatchling · 100 Golden Eggs", priceCents: 499, currency: "usd", name: "Hatchling", eggs: 100, bonus: 0 },
    { id: "eggs_clutch", kind: "eggs", label: "Clutch · 250 Golden Eggs", priceCents: 999, currency: "usd", name: "Clutch", eggs: 250, bonus: 50 },
    { id: "eggs_nest", kind: "eggs", label: "Nest · 600 Golden Eggs", priceCents: 1999, currency: "usd", name: "Nest", eggs: 600, bonus: 200 },
    { id: "eggs_brood", kind: "eggs", label: "Brood · 1,500 Golden Eggs", priceCents: 4999, currency: "usd", name: "Brood", eggs: 1500, bonus: 500 },
];

var BY_ID = {};
ITEMS.forEach(function (item) {
    if (BY_ID[item.id]) {
        throw new Error("shop-catalog: duplicate item id " + item.id);
    }
    BY_ID[item.id] = item;
});

function byId(id) {
    return BY_ID[id] || null;
}

function isPurchasable(kind, id) {
    var item = BY_ID[id];
    return !!item && item.kind === kind;
}

// Eggs granted by an "eggs" pack: exactly the displayed amount. The bonus
// field is marketing copy (savings vs. the base rate), NOT an extra grant.
function eggTotal(item) {
    return item && item.kind === "eggs" ? item.eggs || 0 : 0;
}

// What an item costs in Golden Eggs; 0 for egg packs themselves.
function eggCost(item) {
    return (item && item.costEggs) || 0;
}

export { ITEMS, RARITIES, byId, isPurchasable, eggTotal, eggCost };
